---
title: 'Warum Alexa mir nicht helfen konnte, einen kreativeren Titel zu finden'
date: '2018-02-17'
excerpt: >-
  Kreativität ist das Zukunftsthema – warum sind Maschinen besser in Routinen,
  aber nicht in Innovation? Und wie können menschliche Fähigkeiten wie
  Kreativität in Teams gefördert werden?
metaTitle: Kreativität fördern - Warum Alexa nicht helfen konnte | EmpowerMensch
metaDescription: >-
  Entdecken Sie, wie Unternehmen und Führungskräfte kreatives Denken fördern und
  warum digitale Assistenten wie Alexa daran scheitern, einen kreativeren Titel
  zu ermöglichen. Dieser Artikel beleuchtet praxisnahe Methoden, um Kreativität
  im Team zu stärken.
language: de
slug: warum-alexa-mir-nicht-helfen-konnte-einen-kreativeren-titel-zu-finden
author: Kevin Rassner
---

Eine kürzlich veröffentlichte [Bitkom-Studie](https://www.produktion.de/nachrichten/unternehmen-maerkte/bitkom-digitalisierung-bedroht-34-millionen-jobs-369.html) warnt ausdrücklich vor einem massenhaften Verlust von Jobs durch die Digitalisierung. Die Gründe sind offensichtlich: Maschinen sind in repetitiven Aufgaben mit klar definierbarem Ziel deutlich besser als Menschen. Auch scheinbar komplexe Aufgaben wie Autofahren lässt sich als eine Verkettung von repetitiven Aufgaben (Quer- und Längsregelung) mit klar definierbaren Ziel (keine Unfälle bauen) darstellen. Menschen werden sich daher auf Tätigkeiten fokussieren müssen, welche menschliche und somit nicht oder nur sehr schwer digitalisierbare Fähigkeiten erfordern, allen voran Kreativität.

> The capabilities of the average human being for creativity, for growth, for collaboration, for productivity are far greater than we yet have recognized.

Douglas McGregor, Gründervater moderner Managementtheorie

Doch wie entsteht Kreativität und wie können Unternehmen Wege finden, diese zu fördern?

## Wie entsteht Kreativität?

Die schlechte Nachricht vorneweg: Kreativität ist nicht planbar. Die gute Nachricht ist: Man weiß, welche Eigenschaften und Tätigkeiten Menschen helfen, kreativ zu sein. Folgende Punkte finde ich persönlich besonders wichtig:

### 1. Tagträumen

[Psychologin Rebecca L. McMillian](https://www.huffingtonpost.com/2013/10/03/mind-wandering_n_4024852.html) sieht im Tagträumen einen großen Motor für Kreativität. Die Zeit zu vergessen resultiert oft in kreativen Phasen.

Kreative Menschen brauchen auch Zeit alleine. Besonders oft berichten sie von Einfällen beim Duschen oder Zugfahren.

> Die Musen verlangen Einsamkeit, und nichts verjagt sie eher als der Tumult.

Gotthold Ephraim Lessing, Dichter

### 2. Stimulation

Die ungeplante Aufnahme von Informationen stellt Futter für kreative Gedanken dar. Je fremder die Eindrücke und je neuer die Erfahrungen, desto interessanter sind oft die Einfälle.

> Ich habe keine besondere Begabung, sondern bin nur leidenschaftlich neugierig.

Albert Einstein, Physiker

### 3. Beobachtung

Das Beobachten anderer Menschen löst häufig kreative Prozesse aus. Auch die Inspiration durch Kunst (Muse) ist geschichtlich dokumentiert.

> Try to be one of the people on whom nothing is lost!

[Henry James, Autor](https://public.wsu.edu/~campbelld/amlit/artfiction.html)

### 4. Revolution

Kreativität entsteht oft durch das Hinterfragen von Denkmustern. Ein Denkspiel: Wie sähe der ideale Toaster aus, wenn Brot flüssig wäre?

> Probleme kann man niemals mit derselben Denkweise lösen, durch die sie entstanden sind.

Albert Einstein, Physiker

### 5. Risiko

Kreative Menschen sind oft risikofreudig. Fehler machen und Kreativität gehören zusammen.

> Creatives fail and the really good ones fail often.

Steven Kotler, Autor

---

## Wie fördern Unternehmen Kreativität?

Es gibt zwei Szenarien: Kreativität im Alltag (z.B. Google als Vorbild) und gezielte kreative Prozesse in Workshops oder Projekten.

### Projekt-Beschaffung: 1 Sofa, 5 Nerfguns.

[Alexander Krause](https://www.veraenderungskraft.de/) bestellte für sein Team ein Sofa und Nerfguns, um Distanz und Reize für Kreativität zu schaffen. Besonders für technische Herausforderungen sind solche Ausbrüche wertvoll.

Auch [Mob Programming](https://www.chrislucian.com/2016/05/mob-programming-q.html) fördert Kreativität durch Rollenverteilung und Wechsel von aktiven/passiven Phasen.

### Ausbrechen – mental und geografisch

Gewohnheiten sind Kreativitätskiller. Ein Lokal- oder Ortswechsel kann neue Ideen freisetzen.

[Beispiel Kreativ-Workshop im Museum](/blog/kreativ-workshop-eine-nacht-im-museum)

### Kreativität und Bewegung

Stanford-Studien (Marily Oppezzo) zeigen: Gehen fördert nachweislich kreative Ideen. Unternehmen können Meetings gehend oder im Wechsel an verschiedenen Orten abhalten.

Ideen sollten direkt per Smartphone notiert werden.

---

## Abschließende Worte

Moderne Kreativitätsmethoden sind selten revolutionär. Die wirkliche Revolution muss im Kopf passieren: Rahmenbedingungen und Denkmuster hinterfragen, neue Wege zulassen und Teams Raum für Experimente geben.

---


